import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FilePlus2, FileText, FileWarning } from "lucide-react";
import { toast } from "sonner";
import { api, downloadBlob, errorMessage } from "@/lib/api";
import { badConfig, badRating, CONFIG_ERR, GLASS_FIELDS, GRID_FIELDS, gridDefaults, isSliding, lineExtras, newItem, priceItem, serializeItem, SPEC_FIELDS, toNum, totals as sumTotals } from "@/lib/pricing";
import { ItemsTable } from "@/components/quote/ItemsTable";
import { TotalsPanel } from "@/components/quote/TotalsPanel";
import { QuoteCharges, QuoteDetails } from "@/components/quote/QuoteFields";
import { PreviewDialog } from "@/components/quote/PreviewDialog";
import { ExtractionError, ExtractionProgress } from "@/components/quote/ExtractionStatus";
import { BatchFiles, BatchProgress } from "@/components/quote/BatchProgress";
import { AddFilesDialog, MergeResult } from "@/components/quote/AddFiles";
import { FilesPanel } from "@/components/quote/FilesPanel";
import { useBatch } from "@/lib/useBatch";
import { useAuth } from "@/context/AuthContext";
import { LinesContext, screenSnap, screensFor } from "@/lib/components";
import { EstimateCheckBar } from "@/components/quote/EstimateCheckBar";
import { MarkupBar } from "@/components/quote/MarkupBar";
import { EstimatePanel, ProjectPicker } from "@/components/crm/EstimatePanel";
import { SetAllSeries } from "@/components/quote/SeriesField";

const NO_PRICE = { pricing_mode: "sqft", cost_per_sqft: 0, glass_cost_per_sqft: 0, arch_cost_per_sqft: 0, grid_cost_per_sqft: 0, item_cost: 0 };
import { isStaff, scaleCompany, staffCompanyIn, staffItemIn, staffTypeIn, stripCosts } from "@/lib/staffView";

const NO_ITEMS = "No windows or doors found. You can add items manually.";
const REFUNDED = " Your credit was refunded.";
const QUOTE_FIELDS = ["client_name", "project_name", "project_site", "po_number", "payment_terms", "est_delivery", "notes",
  "shipping", "upgrade_label", "upgrade_amount", "tax_rate"];

function useExtraction(jobId, onDone) {
  const navigate = useNavigate();
  const { setCredits, refreshCredits } = useAuth();
  const [job, setJob] = useState(jobId ? { status: "pending" } : null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!jobId) return undefined;
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/extractions/${jobId}`);
        if (!alive) return;
        setJob(data);
        if (data.status === "done" || data.status === "failed") refreshCredits();
        if (data.status === "done") return doneRef.current(data);
        if (data.status === "failed") return undefined;
      } catch (e) {
        if (!alive) return;
        setJob({ status: "failed", error: errorMessage(e) });
        return undefined;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [jobId, tick, refreshCredits]);
  const retry = async () => {
    try {
      const { data } = await api.post(`/extractions/${jobId}/retry`);
      setCredits(data.balance);
      setJob({ status: "pending", filename: job?.filename });
      setTick((t) => t + 1);
    } catch (e) {
      if (e.response?.status === 402) {
        setCredits(0);
        toast.error(errorMessage(e), { action: { label: "Buy credits", onClick: () => navigate("/billing") } });
      } else toast.error(errorMessage(e));
    }
  };
  return { job, retry };
}

function DrawingChip({ number, fileId, filename }) {
  const open = async () => {
    try {
      const r = await api.get(`/drawings/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="flex items-center gap-3">
      {number && <span data-testid="quote-number" className="rounded-full border border-slate-700 px-3 py-1 font-mono-num text-xs text-slate-200">{number}</span>}
      {fileId && (
        <button data-testid="view-drawing-button" onClick={open} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 transition-colors hover:border-amber-500/60 hover:text-amber-200">
          <FileText className="h-3.5 w-3.5" /> {filename || "View source file"}
        </button>
      )}
    </div>
  );
}

function ScheduleBanner() {
  return (
    <div data-testid="upload-schedule-banner" className="flex items-start gap-3 rounded-xl border border-sky-500/30 bg-sky-500/5 px-4 py-3">
      <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-sky-300" />
      <div className="text-sm">
        <p className="font-semibold text-sky-100">For an exact quote, upload the window schedule</p>
        <p className="mt-0.5 text-slate-400">We read these sizes from the plans. Plan callouts are nominal (close to the rough opening), so check each line marked "Nominal".</p>
      </div>
    </div>
  );
}

const fileName = (q) => `Estimate-${q.number}-${q.client_name}`.normalize("NFKD").replace(/[\u0300-\u036f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g, "")
  .replace(/[^\w.-]+/g, "-").replace(/-+/g, "-").replace(/-$/, "") + ".pdf";

export default function QuoteEditor() {
  const { user } = useAuth();
  const staff = isStaff(user);
  const fromServer = useCallback((its) => (staff ? its.map(staffItemIn) : its).map((it) => ({ ...it, _legacy: it.pricing_mode !== "component" && it.warning !== "No supplier price" })), [staff]);
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const jobId = !id ? params.get("extraction") : null;
  const batchId = !id ? params.get("batch") : null;
  const [readSummary, setReadSummary] = useState(null);
  const [quoteFiles, setQuoteFiles] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [types, setTypes] = useState([]);
  const [company, setCompany] = useState(null);
  const [multiplier, setMultiplier] = useState(null);
  const [multEdited, setMultEdited] = useState(false);
  const [markup, setMarkup] = useState({ name: "", stage: "" });
  const [items, setItems] = useState([]);
  const [q, setQ] = useState({});
  const [meta, setMeta] = useState({ number: null, fileId: null, filename: "" });
  const [clientError, setClientError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [preview, setPreview] = useState(null);
  const [doorItems, setDoorItems] = useState([]);
  const [lines, setLines] = useState({ choices: [], rates: {} });
  const [projectId, setProjectId] = useState(!id ? params.get("project") : null);
  const [locked, setLocked] = useState(false);
  const pickProject = useCallback((p) => {
    setProjectId(p?.id || null);
    if (p) setQ((cur) => ({ ...cur, client_name: p.customer_name, project_name: p.name, project_site: p.site_address || cur.project_site || "" }));
  }, []);
  useEffect(() => {
    const pid = !id ? params.get("project") : null;
    if (pid) api.get(`/projects/${pid}`).then((r) => pickProject(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const matchTimers = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/catalog");
        api.get("/door-items").then((r) => setDoorItems(r.data)).catch(() => setDoorItems([]));
        Promise.all([api.get("/product-lines/choices"), api.get("/shapes/choices"), api.get("/product-lines/defaults"), api.get("/product-lines/screen-defaults")]).then(async ([r, sh, df, sd]) => {
          const byId = async (path) => (staff ? {} : Object.fromEntries((await api.get(path)).data.map((x) => [x.id, x])));
          const [rates, shapeRates] = await Promise.all([byId("/settings/product-lines"), byId("/settings/shapes")]);
          setLines({ choices: r.data, rates, shapes: sh.data, shapeRates, defaults: df.data, screenDefaults: sd.data, loaded: true });
        }).catch(() => setLines((l) => ({ ...l, loaded: true })));
        setTypes(staff ? s.data.types.map(staffTypeIn) : s.data.types);
        setCompany(staff ? staffCompanyIn(s.data.company) : s.data.company);
        if (id) {
          const { data } = await api.get(`/quotes/${id}`);
          const scale = staff ? data.sell_scale ?? 1 : 1;
          if (scale !== 1) {
            setTypes(s.data.types.map((t) => staffTypeIn({ ...t, sell_per_sqft: (t.sell_per_sqft ?? 0) * scale })));
            setCompany(staffCompanyIn(scaleCompany(s.data.company, scale)));
          }
          setItems(fromServer(data.items));
          setQ(Object.fromEntries(QUOTE_FIELDS.map((k) => [k, data[k] ?? ""])));
          setMultiplier(staff ? 1 : data.multiplier);
          setMarkup({ name: data.markup_preset_name || "", stage: data.stage || "draft" });
          setMeta({ number: data.number, fileId: data.source_file_id, filename: "" });
          setQuoteFiles(data.files || []);
        } else {
          const c = s.data.company;
          const defaults = { client_name: "", project_name: "", project_site: "", po_number: "", payment_terms: c.payment_terms || "",
            est_delivery: "", notes: "", shipping: c.default_shipping || 0, upgrade_label: "", upgrade_amount: 0, tax_rate: c.tax_rate || 0 };
          setQ((cur) => ({ ...defaults, ...Object.fromEntries(Object.entries(cur).filter(([, v]) => v !== "" && v !== null && v !== undefined)) }));
          setMultiplier(staff ? 1 : s.data.multiplier);
          api.get("/markup-presets").then((r) => {
            const p = r.data.find((x) => x.is_default);
            if (p) { setMarkup({ name: p.name, stage: "" }); if (!staff && p.multiplier) setMultiplier(p.multiplier); }
          }).catch(() => {});
        }
      } catch (e) {
        toast.error(errorMessage(e, "Quote not found"));
        if (id) navigate("/quotes");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, staff, fromServer]);

  const onExtracted = useCallback((job) => {
    setItems(fromServer(job.items));
    setExtracted(job.items.length);
    setMeta((m) => ({ ...m, fileId: job.file_id, filename: job.filename }));
  }, [fromServer]);
  const { job, retry } = useExtraction(jobId, onExtracted);
  const onBatch = useCallback((b) => {
    const fresh = fromServer(b.items || []);
    setItems((cur) => (cur.some((i) => i.user_edited) ? [...cur, ...fresh.filter((i) => !cur.some((c) => c.id === i.id))] : fresh));
    setExtracted(fresh.length);
    setReadSummary(b.summary);
  }, [fromServer]);
  const { batch, retry: retryFile } = useBatch(batchId, onBatch);
  const onMerged = (data) => {
    setItems(fromServer(data.quote.items));
    setQuoteFiles(data.quote.files || []);
    setMergeResult(data.result);
  };
  const undoMerge = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/undo-merge`);
      setItems(fromServer(data.items));
      setQuoteFiles(data.files || []);
      setMergeResult(null);
      toast.success("Add undone");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const supplierOnly = !!company?.supplier_only;
  const rows = useMemo(() => items.map((it) => {
    const zero = supplierOnly && it.pricing_mode !== "component" && !it._legacy;
    return { ...it, calc: priceItem(zero ? { ...it, ...NO_PRICE } : it, multiplier) };
  }), [items, multiplier, supplierOnly]);
  const totals = useMemo(() => sumTotals(rows, q), [rows, q]);
  const warnings = rows.filter((r) => !(r.price_source === "estimate" && r.calc.edited) && (!r.calc.mapped || !r.calc.sizeOk)).length;

  const setField = (p) => {
    setQ((cur) => ({ ...cur, ...p }));
    if ("client_name" in p) setClientError("");
  };
  const EXTRA_KEYS = ["glazing", "glass_types", "gas", "thickness", "arch", "grids", "grid_type"];
  const withExtras = (it) => ({ ...it, ...lineExtras(it, company) });
  const MATCH_KEYS = ["type_id", "width_in", "height_in", "sku", "model"];
  const applyMatch = (rid, data, sentName) => setItems((list) => list.map((it) => {
    if (it.id !== rid) return it;
    if (data.pricing_mode !== "item") return { ...it, pricing_mode: "sqft", item_cost: 0, catalog_item_id: null, catalog_match: "" };
    const typedSince = sentName !== undefined && (it.product_name || "") !== sentName;
    return { ...it, pricing_mode: "item", item_cost: (staff ? data.item_sell : data.item_cost) ?? 0, catalog_item_id: data.catalog_item_id,
      catalog_match: data.catalog_match, sku: data.sku ?? it.sku, product_name: typedSince ? it.product_name : data.product_name ?? it.product_name };
  }));
  const rematch = (rid, row) => {
    clearTimeout(matchTimers.current[rid]);
    const t = types.find((x) => x.id === row.type_id);
    if (!(t?.category === "door" && t.pricing_mode === "item")) {
      if (row.pricing_mode === "item") applyMatch(rid, { pricing_mode: "sqft" });
      return;
    }
    matchTimers.current[rid] = setTimeout(async () => {
      try {
        const { data } = await api.post("/door-items/match", { type_id: row.type_id, sku: row.sku || "", model: row.model || "",
          width_in: toNum(row.width_in), height_in: toNum(row.height_in), catalog_item_id: row.catalog_item_id || null,
          catalog_match: row.catalog_match || "", product_name: row.product_name || "" });
        applyMatch(rid, data, row.product_name || "");
      } catch { /* keep the line's current price */ }
    }, 350);
  };
  const pickItem = (rid, c) => {
    const cur = itemsRef.current.find((x) => x.id === rid);
    const p = { catalog_item_id: c.id, catalog_match: "picked", sku: c.sku, product_name: c.product_name, user_edited: true };
    setItems((list) => list.map((it) => (it.id === rid ? { ...it, ...p } : it)));
    if (cur) rematch(rid, { ...cur, ...p });
  };
  const patch = (rid, p) => {
    setItems((list) => list.map((it) => {
      if (it.id !== rid) return it;
      const next = { ...it, ...p };
      if ("width_in" in p || "height_in" in p) next.nominal = false;
      if ("sku" in p) next.catalog_match = "";
      if (Object.keys(p).some((k) => k !== "review_flag")) next.user_edited = true;
      if (p.grids === true && !next.grid_color) next.grid_color = next.ext_color || "";
      return EXTRA_KEYS.some((k) => k in p) ? withExtras(next) : next;
    }));
    const cur = itemsRef.current.find((x) => x.id === rid);
    if (cur && MATCH_KEYS.some((k) => k in p)) rematch(rid, { ...cur, ...p, ...("sku" in p ? { catalog_match: "" } : {}) });
  };
  const same = (a, b) => JSON.stringify(a ?? "") === JSON.stringify(b ?? "");
  const blank = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length);
  const compFor = (t, cur) => {
    const want = t?.pricing_mode === "component" || company?.supplier_only;
    const did = t && lines.defaults ? lines.defaults[t.id] : undefined;
    const pl = want ? (did !== undefined ? lines.choices.find((c) => c.id === did) : lines.choices.find((c) => c.type_id === t?.id)) : null;
    const noShape = { shape_id: null, shape_name: "", shape_style: "", _legacy: false, screen_id: null, screen_name: "", screen_pull: "single" };
    const sc = pl && screensFor(lines.choices, pl).find((s) => s.id === lines.screenDefaults?.[t?.id]);
    const scr = sc ? { screen_id: sc.id, screen_name: sc.name, screen_pull: sc.pull === "double" ? "double" : "single" } : {};
    const snap = sc && lines.rates[sc.id] ? { screen: screenSnap(lines.rates[sc.id]) } : {};
    return pl
      ? { pricing_mode: "component", product_line_id: pl.id, component: lines.rates[pl.id] ? { line: lines.rates[pl.id], ...snap } : {}, extras: [], track: "",
        glass_upgrade: "", layout: pl.kind === "slider" ? "" : pl.default_layout, component_unit_sell: null, ...noShape, ...scr }
      : { product_line_id: null, component: {}, ...noShape, ...(cur.pricing_mode === "component" ? { pricing_mode: t?.pricing_mode === "item" ? "item" : "sqft" } : {}) };
  };

  const setType = (rid, tid) => {
    const t = types.find((x) => x.id === tid);
    if (!t) return patch(rid, { type_id: null, type_name: "Unmapped", cost_per_sqft: 0 });
    const cur = items.find((x) => x.id === rid) || {};
    const prevType = types.find((x) => x.id === cur.type_id);
    const prev = prevType?.specs || {};
    const specs = Object.fromEntries(SPEC_FIELDS.map(([k]) => {
      const keep = cur[k] && cur[k] !== (prev[k] || "");
      return [k, keep ? cur[k] : t.specs?.[k] || ""];
    }));
    const prevGlass = prevType?.glass || {};
    const glass = Object.fromEntries(GLASS_FIELDS.map((k) => {
      const keep = !blank(cur[k]) && !same(cur[k], prevGlass[k]);
      const def = t.glass?.[k] ?? (k === "glass_types" ? [] : "");
      return [k, keep ? cur[k] : def];
    }));
    const pick = (o) => Object.fromEntries(GRID_FIELDS.map((k) => [k, o?.[k] ?? gridDefaults()[k]]));
    const noColor = (o) => ({ ...pick(o), grid_color: "" });
    const userGrid = !!cur.grids && !same(noColor(cur), noColor(prevGlass));
    const grid = userGrid ? pick(cur) : pick(t.glass);
    if (grid.grids && !grid.grid_color) grid.grid_color = specs.ext_color || "";
    const sliding = isSliding({ sketch: t.sketch, type_name: t.name });
    const config = sliding ? (cur.config && cur.config !== (prev.config || "") ? cur.config : t.specs?.config || "XO") : "";
    const comp = lines.loaded || (t.pricing_mode !== "component" && !company?.supplier_only) ? compFor(t, cur) : { pending_line: true, _legacy: false };
    patch(rid, { type_id: t.id, type_name: t.name, category: t.category, cost_per_sqft: t.cost_per_sqft, sketch: t.sketch, ...specs, ...glass, ...grid, config, ...comp });
  };

  useEffect(() => {
    if (!lines.loaded) return;
    setItems((prev) => prev.map((it) => (it.pending_line ? { ...it, pending_line: false, ...compFor(types.find((x) => x.id === it.type_id), it) } : it)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.loaded]);

  const save = async (kind) => {
    if (locked && id) {
      toast.error("This estimate is approved and locked. Use Revise to make a new version.");
      return kind === "save" ? null : { id, number: meta.number, client_name: q.client_name };
    }
    if (!q.client_name?.trim()) {
      setClientError("Client name is required");
      toast.error("Client name is required");
      return null;
    }
    const badCfg = items.findIndex((it) => isSliding(it) && badConfig(it.config));
    if (badCfg >= 0) {
      toast.error(`Line ${badCfg + 1}: ${CONFIG_ERR}`);
      return null;
    }
    const badLine = items.findIndex((it) => badRating(it.u_value) || badRating(it.shgc));
    if (badLine >= 0) {
      toast.error(`Line ${badLine + 1}: U-factor and SHGC must be numbers 0 or greater`);
      return null;
    }
    const bad = items.some((it) => ["width_in", "height_in", "qty", "price_override"].some((k) => (toNum(it[k]) ?? 0) < 0))
      || (toNum(q.shipping) ?? 0) < 0 || (toNum(q.tax_rate) ?? 0) < 0 || (toNum(q.tax_rate) ?? 0) > 100;
    if (bad) {
      toast.error("Sizes, quantities, prices, shipping and tax can't be negative (tax max 100%)");
      return null;
    }
    setBusy(kind);
    try {
      const body = { ...q, shipping: toNum(q.shipping) ?? 0, upgrade_amount: toNum(q.upgrade_amount) ?? 0, tax_rate: toNum(q.tax_rate) ?? 0,
        ...(staff || !multEdited ? {} : { multiplier: toNum(multiplier) ?? 0 }), items: items.map((it) => (staff ? stripCosts(serializeItem(it)) : serializeItem(it))),
        source_file_id: meta.fileId || null, ...(!id && batchId ? { batch_id: batchId } : {}), ...(!id && projectId ? { project_id: projectId } : {}) };
      const { data } = id ? await api.put(`/quotes/${id}`, body) : await api.post("/quotes", body);
      setItems(fromServer(data.items));
      setQuoteFiles(data.files || []);
      if (!staff) setMultiplier(data.multiplier);
      setMultEdited(false);
      setMarkup({ name: data.markup_preset_name || "", stage: data.stage || "draft" });
      if (kind === "save") toast.success(id ? "Quote saved" : `Quote ${data.number} saved`);
      return data;
    } catch (e) {
      toast.error(errorMessage(e));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const goSaved = (saved) => !id && navigate(`/quotes/${saved.id}`, { replace: true });
  const onSave = async () => { const s = await save("save"); if (s) goSaved(s); };
  const download = async (saved) => {
    try {
      await downloadBlob(`/quotes/${saved.id}/pdf`, fileName(saved));
    } catch (e) {
      toast.error(errorMessage(e, "Could not generate the PDF"));
    }
  };
  const onPdf = async () => {
    const s = await save("pdf");
    if (!s) return;
    setBusy("pdf");
    await download(s);
    setBusy(null);
    goSaved(s);
  };
  const onPreview = async () => { const s = await save("preview"); if (s) setPreview(s); };
  const closePreview = () => { const s = preview; setPreview(null); if (s) goSaved(s); };

  const reading = (job && (job.status === "pending" || job.status === "running")) || (batchId && !batch?.finished && !batch?.error);
  const failed = (job && job.status === "failed") || batch?.error;
  const batchFailed = batch?.finished ? batch.files.filter((f) => f.status === "failed") : [];

  return (
    <div data-testid="quote-editor-page" className="space-y-8">
      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to={id ? "/quotes" : "/dashboard"} data-testid="editor-back-link" className="inline-flex items-center gap-1.5 text-xs text-slate-500 transition-colors hover:text-slate-300">
            <ArrowLeft className="h-3.5 w-3.5" /> {id ? "All quotes" : "New quote"}
          </Link>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">{id ? "Edit quote" : "Review & price"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <DrawingChip number={meta.number} fileId={quoteFiles.length > 1 ? null : meta.fileId} filename={meta.filename} />
          {!reading && !loading && (
            <button data-testid="add-files-button" onClick={() => (id ? setAddOpen(true) : toast.error("Save the quote first, then add files"))}
              title={id ? "Add schedules or plans to this quote" : "Save the quote first"}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-200">
              <FilePlus2 className="h-3.5 w-3.5" /> Add files
            </button>
          )}
        </div>
      </div>

      {reading && batchId && <BatchProgress batch={batch} />}
      {reading && !batchId && <ExtractionProgress filename={job.filename} phase={job.phase} estimate={job.kind === "estimate"} />}
      {failed && <ExtractionError message={job?.error || batch?.error} onRetry={job ? retry : () => window.location.reload()} />}

      {!reading && !failed && !loading && (
        <div className="rise rise-1 grid gap-8 xl:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 space-y-6">
            {id ? <EstimatePanel key={id} quoteId={id} onLocked={setLocked} /> : <ProjectPicker value={projectId} onPick={pickProject} />}
            <QuoteDetails q={q} set={setField} clientError={clientError} />
            {mergeResult && <MergeResult result={mergeResult} onUndo={undoMerge} />}
            {((job?.status === "done" && job.has_schedule === false) || readSummary?.has_schedule === false) && <ScheduleBanner />}
            {readSummary && (
              <p data-testid="batch-read-summary" className="text-sm font-medium text-slate-300">{readSummary.text}</p>
            )}
            {readSummary?.notice && (
              <p data-testid="plan-credit-notice" className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">
                {readSummary.notice} <a href="/billing" data-testid="plan-credit-buy-link" className="font-semibold underline">Buy credits</a>
              </p>
            )}
            {batchFailed.length > 0 && <BatchFiles batch={{ ...batch, files: batchFailed }} onRetry={retryFile} />}
            {job?.kind === "estimate" && job.status === "done" && <EstimateCheckBar job={job} rows={rows} />}
            {job?.status === "done" && job.skipped_doors > 0 && (
              <p data-testid="skipped-doors-banner" className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">
                {job.skipped_doors} interior/garage door{job.skipped_doors === 1 ? "" : "s"} skipped, not offered
              </p>
            )}
            {extracted === 0 && !job?.not_estimate && (
              <p data-testid="no-items-found-message" className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">{NO_ITEMS}{batch && !batch.refunded ? "" : REFUNDED}</p>
            )}
            {extracted > 0 && (
              <p data-testid="extraction-summary" className="text-sm text-slate-400">
                Found <span className="font-semibold text-slate-200">{extracted}</span> item{extracted === 1 ? "" : "s"}. Check highlighted cells before saving.
              </p>
            )}
            <MarkupBar quoteId={id} markup={markup} owner={!staff} multiplier={multiplier}
              onOverride={(v) => { setMultiplier(v); setMultEdited(true); }}
              onRepriced={(data) => { setItems(fromServer(data.items)); if (!staff) setMultiplier(data.multiplier); setMultEdited(false); setMarkup({ name: data.markup_preset_name || "", stage: data.stage || "draft" }); }} />
            <LinesContext.Provider value={{ ...lines, supplierOnly, quoteId: id }}>
            <SetAllSeries rows={items} onPatchAll={(fn) => setItems((l) => l.map((r) => { const p = fn(r); return p ? { ...r, ...p, user_edited: true } : r; }))} />
            <ItemsTable rows={rows} types={types} onPatch={patch} onType={setType} company={company} doorItems={doorItems} onPick={pickItem}
              onDelete={(rid) => setItems((l) => l.filter((x) => x.id !== rid))}
              onAdd={() => setItems((l) => [...l, newItem()])}
              emptyNote={extracted === 0 ? NO_ITEMS : undefined} />
            </LinesContext.Provider>
            <QuoteCharges q={q} set={setField} />
            {id && <FilesPanel quoteId={id} files={quoteFiles} items={items} onChanged={(data) => {
              setItems(fromServer(data.items)); setQuoteFiles(data.files || []); setMeta((m) => ({ ...m, fileId: data.source_file_id }));
            }} />}
          </div>
          <TotalsPanel totals={totals} multiplier={multiplier} taxRate={q.tax_rate} upgradeLabel={q.upgrade_label} count={rows.length}
            warnings={warnings} saved={!!id} onSave={onSave} onPdf={onPdf} onPreview={onPreview} busy={busy} />
        </div>
      )}
      <PreviewDialog quote={preview} onClose={closePreview} onDownload={() => preview && download(preview)} />
      <AddFilesDialog open={addOpen} onClose={() => setAddOpen(false)} prepare={() => save("silent")} onMerged={onMerged} />
    </div>
  );
}
